<script setup lang="ts">
import type { RecordLinkInfo } from './fields';
import axios from 'axios';
import { computed, ref } from 'vue';
import { SphButton } from '@/components/base/button';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';
import { recordLinkFieldLabel } from './fields';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    memberId: number
    recordLink: RecordLinkInfo
}>();

const { i18n } = useI18n();
const { notify } = useNotifications();

const claiming = ref(false);

const linkedClubs = computed(() => [...new Set(props.recordLink.linkedRecords.map((record) => record.institutionName))].join(', '));

const divergentFieldNames = computed(() =>
    props.recordLink.divergentFields.map((field) => i18n.t(recordLinkFieldLabel(field))).join(', '),
);

function claim() {
    claiming.value = true;
    axios.post(`/club/members/${props.memberId}/record-link/claim`)
        .then(() => {
            notify.success(i18n.t('Der Hauptdatensatz liegt jetzt bei diesem Verein.'), {}, { saveInSession: true });
            window.location.reload();
        })
        .catch((error) => {
            notify.error(error?.response?.data?.error?.message ?? i18n.t('Der Hauptdatensatz konnte nicht beansprucht werden.'));
            claiming.value = false;
        });
}
</script>

<template>
    <div
        class="kgr-alert kgr-alert--info kgr-row kgr-row--bigMargin"
    >
        <div class="kgr-row kgr-text">
            <template v-if="recordLink.isMain">
                {{ $t('Dieser Datensatz ist der Hauptdatensatz dieser Person.') }}
                {{ $t('Verknüpfte Datensätze bestehen bei:') }} {{ linkedClubs }}
            </template>
            <template v-else-if="recordLink.state === 'resolved'">
                {{ $t('Diese Person ist in mehreren Vereinen erfasst.') }}
                {{ $t('Der Hauptdatensatz liegt bei:') }} {{ recordLink.mainInstitutionName }}
            </template>
            <template v-else>
                {{ $t('Diese Person ist in mehreren Vereinen erfasst.') }}
                {{ $t('Verknüpfte Datensätze bestehen bei:') }} {{ linkedClubs }}
                {{ $t('Es ist noch kein Hauptdatensatz festgelegt.') }}
            </template>
        </div>
        <div
            v-if="divergentFieldNames"
            class="kgr-row kgr-text"
        >
            {{ $t('Abweichende Felder:') }} {{ divergentFieldNames }}
        </div>
        <SphButton
            v-if="recordLink.canClaim"
            :label="$t('Hauptdatensatz beanspruchen')"
            :loading="claiming"
            size="sm"
            @click="claim"
        />
    </div>
</template>
